/* tslint:disable */
/* eslint-disable */

import { ApiApiAxiosParamCreator,ApiApiFactory,ApiApiRoutes } from "./api";
import { Configuration } from "./configuration";
import type { ExtendedAxiosRequestConfig } from "./common"
import type { RequestArgs } from "./base";
import axios, {AxiosError} from "axios";
import type {
AxiosInstance,
AxiosPromise,
AxiosRequestConfig,
AxiosResponse, CreateAxiosDefaults,
InternalAxiosRequestConfig
} from "axios";
import {type ResponseDTO} from "@unatec/arq-web-comun-dto";  
import {Deferred} from "devextreme/core/utils/deferred";
import CustomStore from "devextreme/data/custom_store";
import type {Options} from "devextreme/data/custom_store";
import type {LoadOptions} from "devextreme/data";
import type { DXLoadOptions, LoadResult } from "./api";
import { type IApiCallConfiguration } from "@services/http"

/* ******** Types and interfaces ******** */
type ApiClientParamCreator = ReturnType<typeof ApiApiAxiosParamCreator>; 
type ApiClientFactory = ReturnType<typeof ApiApiFactory>; 
type DataTypeFromAxiosResponse<T> = T extends { data: infer U } ? U : never;
export type ChainingFunction<TInput extends unknown[], TOutput> = (...args: TInput) => Promise<ApiCallChain<TInput, TOutput>>;
type DataTypeFromStandardDTO<T> = Required<T> extends { Data: infer U } ? U : never;
export type ApiStoreOptions<TItem,TKey> = Omit<Options<TItem,TKey>, "load" | "update" | "insert"> & {
  load: (options: DXLoadOptions) => Promise<LoadResult>,
  update?: (key: TKey, values: Partial<TItem>) => Promise<TItem | null>,
  insert?: (values: TItem) => Promise<TItem | TKey | null>,
};

export interface RequestInterceptorCallbacks {
  onFullfilled: (response: InternalAxiosRequestConfig) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>
  onRejected: (error: AxiosError) => unknown;
}

export interface ResponseInterceptorCallbacks {
  onResponse: (response: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>;
  onError: (error: AxiosError) => unknown;
}

export interface StandardCallOptions  extends IApiCallConfiguration {

}

export type StandardCallAxiosRequestConfig<TOperationParams> = AxiosRequestConfig<TOperationParams> & ExtendedAxiosRequestConfig & StandardCallOptions;
        

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface ApiCallChain<TInput extends unknown[], TOutput> {
  axiosConfiguration: StandardCallAxiosRequestConfig<TInput>;
  axiosInstance: AxiosInstance; 
}

/* ******** Data ******** */
let apiEndPoints: ApiClientParamCreator | null = null;
let baseAxiosConfig: CreateAxiosDefaults; 
let baseParamConfig: Configuration; 
let baseIntegrationConfig: ApiIntegrationConfig;
              
      
              
/* ******** Functions ******** */
function getEndpointRoute(operationId: keyof ApiClientParamCreator): string {
  return `${baseAxiosConfig.baseURL}${ ApiApiRoutes[operationId] }`;
}

function createAxiosInstance() {
  return axios.create(baseAxiosConfig);
} 

function withEndpoint<
    TOperationId extends keyof ApiClientParamCreator,
    TOperationParamCreator extends ApiClientParamCreator[TOperationId],
    TOperation extends ApiClientFactory[TOperationId],
    TOperationParams extends Parameters<TOperationParamCreator>,
    TOperationResult extends ReturnType<TOperation>,
>(operationId: TOperationId, standardCallOptions?: StandardCallOptions): ChainingFunction<TOperationParams, TOperationResult> {

    return async (...args: TOperationParams): Promise<ApiCallChain<TOperationParams, TOperationResult>> => {
        const operation = apiEndPoints![operationId] as unknown as ((...args: TOperationParams) => Promise<RequestArgs>);
        const requestArgs = await operation(...args);

        return {
            axiosConfiguration: {url: requestArgs.url, ...{extensions: {}}, ...requestArgs.options,...standardCallOptions} as StandardCallAxiosRequestConfig<TOperationParams>,
            axiosInstance: createAxiosInstance()
        }
    }
}
       
function withRequestInterceptor<TInput extends unknown[], TOutput, TNewInput extends unknown[], TNewOutput>(chain: ChainingFunction<TInput, TOutput>, callbacks: RequestInterceptorCallbacks)
    : (...args: TInput) => Promise<ApiCallChain<TNewInput, AxiosPromise<TNewOutput>>> {
    return (async (...args: TInput) => {
        const config = await chain(...args);
        config.axiosInstance.interceptors.request.use(callbacks.onFullfilled, callbacks.onRejected);
        return config;
    }) as unknown as (...args: TInput) => Promise<ApiCallChain<TNewInput, AxiosPromise<TNewOutput>>>;
}

function withResponseInterceptor<TInput extends unknown[], TOutput, TNewOutput>(chain: ChainingFunction<TInput, TOutput>, callbacks: ResponseInterceptorCallbacks)
    : (...args: TInput) => Promise<ApiCallChain<TInput, AxiosPromise<TNewOutput>>> {
    return async (...args: TInput) => {
        const config = await chain(...args);
        config.axiosInstance.interceptors.response.use(callbacks.onResponse, callbacks.onError);
        return config;
    }
}


function withInterceptors<TInput extends unknown[],TOutput, TNewInput extends unknown[], TNewOutput>
    (chain: ChainingFunction<TInput, TOutput>, callbacks: RequestInterceptorCallbacks & ResponseInterceptorCallbacks)
    : (...args: TInput) => Promise<ApiCallChain<TNewInput, AxiosPromise<TNewOutput>>> {
    return (async (...args: TInput) => {
        const config = await chain(...args);
        config.axiosInstance.interceptors.request.use(callbacks.onFullfilled, callbacks.onRejected);
        config.axiosInstance.interceptors.response.use(callbacks.onResponse, callbacks.onError);
        return config;
    }) as unknown as (...args: TInput) => Promise<ApiCallChain<TNewInput, AxiosPromise<TNewOutput>>>;
}     

async function call<TInput extends unknown[], TOutput, TFinalOutput extends DataTypeFromAxiosResponse<Awaited<TOutput>>
>(chainFn: ChainingFunction<TInput, TOutput>, ...args: TInput)
    : Promise<TFinalOutput> {

    const chain = await chainFn(...args);
    const result = await chain.axiosInstance.request<TInput, TFinalOutput>(chain.axiosConfiguration);
    return (result as unknown as Record<string, unknown>).data as TFinalOutput;
}          

function prepareStandardApi<TOperationId extends keyof ApiClientParamCreator,
    TOperationParamCreator extends ApiClientParamCreator[TOperationId],
    TOperation extends ApiClientFactory[TOperationId],
    TOperationParams extends Parameters<TOperationParamCreator>,
    TOperationResult extends ReturnType<TOperation>,
    TOutput extends DataTypeFromStandardDTO<DataTypeFromAxiosResponse<Awaited<TOperationResult>>>
   >(operation: TOperationId, standardCallOptions?: StandardCallOptions): (...args: TOperationParams) => Promise<ApiCallChain<TOperationParams, TOperationResult>> {
    let endPoint = withEndpoint(operation, standardCallOptions);
    if (baseIntegrationConfig.initialMiddleware != null) {
        for (const initialMiddleWare of baseIntegrationConfig.initialMiddleware) {
            endPoint = initialMiddleWare(endPoint as any);
        }
    }
  let dto = withStandardDTO(endPoint);
                                              
    if (baseIntegrationConfig.finalMiddleware != null) {
        for (const finalMiddleware of baseIntegrationConfig.finalMiddleware) {
            dto = finalMiddleware(dto as any);
        }
    }
                                              
    return dto as unknown as (...args: TOperationParams) => Promise<ApiCallChain<TOperationParams, TOperationResult>>
}
                                              
function toStandardApiFunction<TOperationId extends keyof ApiClientParamCreator,
    TOperationParamCreator extends ApiClientParamCreator[TOperationId],
    TOperation extends ApiClientFactory[TOperationId],
    TOperationParams extends Parameters<TOperationParamCreator>,
    TOperationResult extends ReturnType<TOperation>,
    TOutput extends DataTypeFromStandardDTO<DataTypeFromAxiosResponse<Awaited<TOperationResult>>>
>(operation: TOperationId, standardCallOptions?: StandardCallOptions): Promise<(...args: TOperationParams) => Promise<TOutput>> {
    const prepared = prepareStandardApi(operation, standardCallOptions) as (...args: TOperationParams) => Promise<ApiCallChain<TOperationParams, TOperationResult>>;
    return Promise.resolve(((...args: TOperationParams) => call(prepared, ...args)) as (...args: TOperationParams) => Promise<TOutput>);
}                                              

function withStandardDTO<
    TInput extends unknown[],
    TOutput,
    TInner extends DataTypeFromStandardDTO<DataTypeFromAxiosResponse<Awaited<TOutput>>>
>(chainFn: ChainingFunction<TInput, TOutput>): (...args: TInput) => Promise<ApiCallChain<TInput, AxiosPromise<TInner>>> {

        return withResponseInterceptor<TInput, TOutput, TInner>(chainFn,
        {
            onResponse: (response) => {
				const dtoResponse = response.data as ResponseDTO<TInner, unknown, unknown, unknown>;
                if (dtoResponse.Ok != null && dtoResponse.Status != null) {
                    if (!dtoResponse.Ok) {
                        return Promise.reject(response);
                    }
                    response.data = dtoResponse.Data;
                }

                return response;             
            },
            onError: (error : AxiosError) => {
               if ((error.response?.data as any|null)?.Ok != null) {
                   const dtoResponse = error.response!.data as ResponseDTO<TInner, unknown, unknown, unknown>;
                   (error as any).message = dtoResponse.Message;
               }
                   
               return Promise.reject(error);
            }
        }
    )
}

async function callStandardApi<TOperationId extends keyof ApiClientParamCreator,
    TOperationParamCreator extends ApiClientParamCreator[TOperationId],
    TOperation extends ApiClientFactory[TOperationId],
    TOperationParams extends Parameters<TOperationParamCreator>,
    TOperationResult extends ReturnType<TOperation>,
    TOutput extends DataTypeFromStandardDTO<DataTypeFromAxiosResponse<Awaited<TOperationResult>>>
>(operation: TOperationId, standardCallOptions: StandardCallOptions, ...args: TOperationParams): Promise<TOutput> {

    const prepared = prepareStandardApi(operation, standardCallOptions) as (...args: TOperationParams) => Promise<ApiCallChain<TOperationParams, TOperationResult>>;
    return await call(prepared, ...args) as TOutput
}

function apiStore<TItem,TKey>(options: ApiStoreOptions<TItem,TKey>): CustomStore<TItem,TKey> {
  
    const customLoad = (loadOptions: LoadOptions): Promise<LoadResult> => options.load!(loadOptions as unknown as DXLoadOptions);

    
    const dxOptions = {...options, load: customLoad, insert: undefined, update: undefined};
    if (options.update != null) {
        const customUpdate = async (key: TKey, values: Partial<TItem>): Promise<TItem> => {
            return (await options.update!(key, values)) as unknown as TItem;
        }
        (dxOptions.update as any) = customUpdate;
    }
    
    if (options.insert != null) {
        const customInsert = async (values: TItem): Promise<TItem> => {
            const maybeItem = await options.insert!(values);
            if (typeof maybeItem === "object") {
                return maybeItem as TItem
            } else if (maybeItem != null) {
                const retVal =  {...values};
                if (Array.isArray(options.key) && Array.isArray(maybeItem)) {
                    for (const partialKey of options.key) {
                        (retVal as unknown as Record<string, unknown>)[partialKey] = (maybeItem as unknown[]).splice(0,1)[0];
                    }
                }
                else {
                    (retVal as unknown as Record<string, unknown>)[options.key as string] = maybeItem;    
                }
                
                return retVal as TItem;
            }
            
            return null as unknown as TItem;
        }

        (dxOptions as any).insert = customInsert;
    }
    
    const handler = dxOptions.errorHandler;
    dxOptions.errorHandler = (e: Error) => {
        if (baseIntegrationConfig.dxDataErrorHandler != null)
            baseIntegrationConfig.dxDataErrorHandler(e)
        if (handler != null) 
            handler(e);
    };

	
    return new CustomStore<TItem,TKey>(dxOptions);
}

/* ******** Composable ******* */
export function useApi() {
  if (apiEndPoints == null) {
      apiEndPoints = ApiApiAxiosParamCreator(baseParamConfig);
  }

  // noinspection JSUnusedGlobalSymbols
  return {
      withEndpoint,
      prepareStandardApi,
      callStandardApi,
      toStandardApiFunction,
      apiStore,
      createAxiosInstance,
      getEndpointRoute
  }
}             
                                                    


export interface ApiIntegrationConfig {
    initialMiddleware? : ((chain: ChainingFunction<unknown[], unknown>) => ((...args: unknown[]) => Promise<ApiCallChain<unknown[], unknown>>))[]
    finalMiddleware? : ((chain: ChainingFunction<unknown[], unknown>) => ((...args: unknown[]) => Promise<ApiCallChain<unknown[], unknown>>))[]      
    dxDataErrorHandler?: (error: any) => void;
}

export function configureApi(axiosConfig: CreateAxiosDefaults, paramConfig: Configuration, 
                             integrationConfig: ApiIntegrationConfig) {
    baseAxiosConfig = axiosConfig;
    baseParamConfig = paramConfig;
    baseIntegrationConfig = integrationConfig;
} 
  